import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ConfigurationTypeaheadComponent } from '@app/modules/configuration/configuration-typeahead/configuration-typeahead.component';
import { PartnersUtilsService } from '@app/modules/partners/partners-utils.service';
import { Bundle, Partner } from '@core/core.types';
import { LoadPartnersByIds } from '@core/store/partners/partners.actions';
import { PartnersState } from '@core/store/partners/partners.state';
import { NgbActiveModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { DateTimeComponent } from '@shared/components/date-time/date-time.component';
import { PartnerTypeaheadComponent } from '@shared/components/partner-typeahead/partner-typeahead.component';
import { SelectOptionComponent } from '@shared/components/select/select-option/select-option.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { ToastService } from '@shared/components/toast-container/toast.service';
import { ValidationErrorComponent } from '@shared/components/validation-error/validation-error.component';
import { DisplayNamePipe } from '@shared/pipes/display-name.pipe';
import { DateTimeType } from '@shared/shared.types';
import { crossFieldLowerHigherValidator } from '@shared/validators/cross-field-lower-higher-validator';
import moment from 'moment';
import {
  catchError,
  combineLatest,
  filter,
  first,
  map,
  Observable,
  of,
  startWith,
  switchMap,
} from 'rxjs';
import {
  CreateDiscountAction,
  UpdateDiscountAction,
} from '../../../store/discount.actions';
import { DiscountState } from '../../../store/discount.state';
import {
  CreateDiscountPayload,
  Discount,
  DiscountPriceType,
  DiscountType,
  ValueType,
} from '../../../types/pricing.types';
import { crossFieldPercentValidator } from '../../../validators/cross-field-percent-validator';

@Component({
  selector: 'app-discount-form',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    SelectComponent,
    SelectOptionComponent,
    DateTimeComponent,
    TranslatePipe,
    AsyncPipe,
    DisplayNamePipe,
    PartnerTypeaheadComponent,
    ConfigurationTypeaheadComponent,
    ValidationErrorComponent,
    NgbTooltipModule,
  ],
  templateUrl: './discount-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './discount-form.component.scss',
})
export class DiscountFormComponent implements OnInit {
  protected modalRef = inject(NgbActiveModal);
  protected discountId = signal<string>(null);
  protected partnerId = signal<string>(null);
  protected serviceDataReferenceKey = signal<string>(null);
  protected readonly ValueType = ValueType;
  protected readonly DiscountType = DiscountType;
  protected readonly DiscountPriceType = DiscountPriceType;

  protected allPartnerBundles$: Observable<Bundle[]>;
  protected filteredPartnerBundles$: Observable<Bundle[]>;
  protected selectedPartners$: Observable<Partner[]>;
  protected minDate =
    moment().date() <= 3
      ? moment().subtract(1, 'month').startOf('month')
      : moment().startOf('month');
  protected partnerSearchControl = new FormControl<string>(null);

  private fb = inject(FormBuilder);
  private store = inject(Store);
  private destroyRef = inject(DestroyRef);
  private partnersUtilsService = inject(PartnersUtilsService);
  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);

  discountForm = this.fb.group(
    {
      id: [''],
      partnerIds: this.fb.nonNullable.control<string[]>(
        [],
        [Validators.required]
      ),
      discountType: [DiscountType.General, Validators.required],
      priceType: [DiscountPriceType.WSP, Validators.required],
      bundleIds: new FormControl<string[]>([]),
      serviceDataReferenceKey: [''],
      discountValue: new FormControl<number>(null, [
        Validators.required,
        Validators.min(1),
      ]),
      valueType: [ValueType.Percentage, Validators.required],
      validFrom: [moment(), Validators.required],
      validTo: [moment().add(1, 'month'), Validators.required],
      reason: ['', Validators.required],
    },
    {
      validators: [
        crossFieldLowerHigherValidator('validFrom', 'validTo', true),
        crossFieldPercentValidator('valueType', 'discountValue'),
      ],
    }
  );

  public c(controlName: string) {
    return this.discountForm.controls[controlName];
  }

  ngOnInit(): void {
    if (this.discountId()) {
      this.editDiscount();
    } else {
      this.createDiscount();
    }

    this.allPartnerBundles$ = this.getAllPartnerBundles();
    this.filteredPartnerBundles$ = this.getFilteredPartnerBundles();
    this.selectedPartners$ = this.getSelectedPartners();
  }

  private editDiscount() {
    this.store
      .select(DiscountState.getDiscountById)
      .pipe(
        map((getById) => getById(this.discountId())),
        first()
      )
      .subscribe((discount) => {
        this.discountForm.patchValue({
          ...discount,
          partnerIds: discount.partnerId ? [discount.partnerId] : [],
          validFrom: moment(discount.validFrom),
          validTo: moment(discount.validTo),
        });
      });

    this.c('discountType').disable();
    this.c('priceType').disable();
    this.c('serviceDataReferenceKey').disable();
  }

  private createDiscount() {
    this.c('discountType')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (value === DiscountType.General) {
          this.c('bundleIds').setValidators([Validators.required]);
          this.c('serviceDataReferenceKey').clearValidators();
          this.c('serviceDataReferenceKey').updateValueAndValidity();
          this.c('partnerIds').setValue([]);
        } else if (value === DiscountType.Individual) {
          this.c('serviceDataReferenceKey').setValidators([
            Validators.required,
          ]);
          this.c('bundleIds').clearValidators();
          this.c('bundleIds').updateValueAndValidity();
          this.c('partnerIds').setValue([]);
        }
      });

    this.c('partnerIds')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.c('bundleIds').setValue([]);
      });

    if (this.serviceDataReferenceKey() && this.partnerId()) {
      this.c('serviceDataReferenceKey').setValue(
        this.serviceDataReferenceKey()
      );
      this.c('partnerIds').setValue([this.partnerId()]);
      this.c('discountType').setValue(DiscountType.Individual);
      this.c('discountType').disable();
      this.c('serviceDataReferenceKey').disable();
    } else {
      this.c('discountType').setValue(DiscountType.General);
    }

    if (!this.serviceDataReferenceKey() && this.partnerId()) {
      this.c('partnerIds').setValue([this.partnerId()]);
      this.c('discountType').setValue(DiscountType.General);
      this.c('discountType').disable();
      this.c('bundleIds').enable();
    }
  }

  private getAllPartnerBundles(): Observable<Bundle[]> {
    const partnerIdsControl = this.discountForm.get('partnerIds');

    return partnerIdsControl.valueChanges
      .pipe(startWith(partnerIdsControl.value))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((partnerIds: string[]) => {
          if (!partnerIds?.length) {
            return of([]);
          }
          return combineLatest(
            partnerIds.map((partnerId) =>
              this.partnersUtilsService.getPartnerById$(partnerId).pipe(
                first(),
                filter((partner: Partner) => !!partner),
                switchMap((partner: Partner) =>
                  this.partnersUtilsService
                    .getPartnerBundles(partner.bundleConfigurations || [])
                    .pipe(
                      catchError((error) => {
                        console.error('Error loading partner bundles:', error);
                        return of([]);
                      })
                    )
                ),
                catchError((error) => {
                  console.error('Error loading partner:', error);
                  return of([]);
                })
              )
            )
          ).pipe(
            map((bundleGroups: Bundle[][]) => {
              return bundleGroups
                .flat()
                .filter(
                  (bundle, index, bundles) =>
                    bundles.findIndex(
                      (existingBundle) => existingBundle.id === bundle.id
                    ) === index
                );
            })
          );
        }),
        map((bundles: Bundle[]) => {
          const sortedBundles = bundles.sort((a, b) =>
            a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1
          );
          return sortedBundles;
        }),
        catchError((error) => {
          console.error('Error in getAllPartnerBundles:', error);
          return of([]);
        })
      );
  }

  private getSelectedPartners(): Observable<Partner[]> {
    const partnerIdsControl = this.discountForm.get('partnerIds');

    return partnerIdsControl.valueChanges.pipe(
      startWith(partnerIdsControl.value),
      takeUntilDestroyed(this.destroyRef),
      switchMap((partnerIds: string[]) => {
        if (!partnerIds?.length) {
          return of([]);
        }

        return this.store
          .dispatch(new LoadPartnersByIds(partnerIds))
          .pipe(
            switchMap(() =>
              this.store
                .select(PartnersState.getPartnersByIds)
                .pipe(
                  map((getPartnersByIds) =>
                    getPartnersByIds(partnerIds).filter((partner) => !!partner)
                  )
                )
            )
          );
      }),
      catchError((error) => {
        console.error('Error in getSelectedPartners:', error);
        return of([]);
      })
    );
  }

  private getFilteredPartnerBundles(): Observable<Bundle[]> {
    const bundleIdsControl = this.discountForm.get('bundleIds');

    return combineLatest([
      this.allPartnerBundles$,
      bundleIdsControl.valueChanges.pipe(
        startWith(bundleIdsControl.value || [])
      ),
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
      map(([bundles, selectedBundleIds]: [Bundle[], string[]]) => {
        return bundles
          .filter((bundle) => !selectedBundleIds.includes(bundle.id))
          .sort((a, b) =>
            a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1
          );
      }),
      catchError((error) => {
        console.error('Error in getFilteredPartnerBundles:', error);
        return of([]);
      })
    );
  }

  protected onBundleSelect(bundleId: string) {
    this.c('bundleIds').setValue(
      [...this.c('bundleIds').value, bundleId].filter(
        (id, index, arr) => arr.indexOf(id) === index
      )
    );
  }

  protected onPartnerSelect(partner: Partner) {
    if (!partner?.id) {
      return;
    }

    if (this.c('discountType').value === DiscountType.Individual) {
      this.c('partnerIds').setValue([partner.id]);
    } else {
      this.c('bundleIds').setValue([]);
      this.c('partnerIds').setValue(
        [...this.c('partnerIds').value, partner.id].filter(
          (id, index, arr) => arr.indexOf(id) === index
        )
      );
    }

    this.partnerSearchControl.setValue(null);
  }

  protected removePartner(partnerId: string) {
    this.c('partnerIds').setValue(
      [...this.c('partnerIds').value].filter((id) => id !== partnerId)
    );
  }

  protected addAllBundles() {
    const bundles = this.getAllPartnerBundles();
    bundles.subscribe((bundles) => {
      this.c('bundleIds').setValue(bundles.map((bundle) => bundle.id));
    });
  }

  protected removeBundle(bundleId: string) {
    this.c('bundleIds').setValue(
      [...this.c('bundleIds').value].filter((id) => id !== bundleId)
    );
  }

  protected submit() {
    this.discountForm.markAllAsTouched();
    if (this.discountForm.valid) {
      const formData = this.discountForm.getRawValue();
      const selectedPartnerIds = formData.partnerIds ?? [];

      const discountPayload: Discount = {
        id: formData.id,
        partnerId: selectedPartnerIds[0],
        discountType: formData.discountType,
        priceType: formData.priceType,
        discountValue: formData.discountValue,
        reason: formData.reason,
        validFrom: formData.validFrom.toDate(),
        validTo: formData.validTo.toDate(),
        valueType: formData.valueType,
        active: true,
        ...(formData.discountType === DiscountType.Individual
          ? {
              serviceDataReferenceKey: formData.serviceDataReferenceKey,
            }
          : {
              bundleIds: formData.bundleIds,
            }),
      };

      const action = this.discountId()
        ? new UpdateDiscountAction(discountPayload)
        : new CreateDiscountAction({
            ...discountPayload,
            partnerIds: selectedPartnerIds,
          } satisfies CreateDiscountPayload);

      this.store.dispatch(action).subscribe({
        next: () => {
          this.modalRef.close();
        },
        error: (error) => {
          this.toastService.danger(
            error.error?.message,
            this.translateService.instant('pricing.discount.form.errors.save'),
            0
          );
          return of(null);
        },
      });
    }
  }

  protected readonly DateTimeType = DateTimeType;
  protected readonly moment = moment;
}
